import { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';

// Function to calculate total minutes from work hours list
const calculateWorkHours = (workHoursList) => {
  let totalMinutes = 0;

  workHoursList.forEach(timeStr => {
    const hoursMatch = timeStr.match(/(\d+)\s*hours?/);
    const minutesMatch = timeStr.match(/(\d+)\s*minutes?/);

    const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;

    totalMinutes += (hours * 60) + minutes;
  });

  return totalMinutes;
};

// Function to calculate daily average hours based on total minutes and workdays
const calculateDailyAverageHours = (totalMinutes, workdays) => {
  const avgMinutesPerDay = totalMinutes / workdays;
  const avgHours = Math.floor(avgMinutesPerDay / 60);
  const avgRemainingMinutes = Math.floor(avgMinutesPerDay % 60);
  return { avgHours, avgRemainingMinutes };
};

// Function to count the number of workdays excluding weekends
const countWorkdays = (startDate, endDate) => {
  let count = 0;
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclude Sundays (0) and Saturdays (6)
      count++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return count;
};

const useWorkHoursSummary = (user) => {
  const [totalHours, setTotalHours] = useState({ hours: 0, minutes: 0 });
  const [avgHours, setAvgHours] = useState({ hours: 0, minutes: 0 });

  const db = getFirestore();

  useEffect(() => {
    const fetchWorkHours = async () => {
      if (!user || !user.id) return;

      const tasksRef = collection(db, 'assignedTasks');
      const q = query(tasksRef, where('employeeId', '==', user.id));
      const querySnapshot = await getDocs(q);

      const workHoursList = querySnapshot.docs.map(doc => doc.data().workHours);

      const totalMinutes = calculateWorkHours(workHoursList);

      // Calculate total hours and remaining minutes
      const totalHoursObj = {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
      };
      setTotalHours(totalHoursObj);

      // Get joining date and calculate days worked
      const employeeDocRef = doc(db, 'employees', user.id);
      const employeeDoc = await getDoc(employeeDocRef);
      const employeeData = employeeDoc.data();
      const joiningDateStr = employeeData?.joiningDate;
      
      if (!joiningDateStr) return; // Handle case where joiningDate is missing

      const joiningDate = new Date(joiningDateStr);
      const today = new Date();

      // Calculate the number of workdays excluding weekends
      const workdays = countWorkdays(joiningDate, today);

      // Calculate daily average hours
      const avgHoursObj = calculateDailyAverageHours(totalMinutes, workdays);
      setAvgHours(avgHoursObj);

      // Update employee's total hours and avgHours in Firestore
      await updateDoc(employeeDocRef, {
        totalHours: `${totalHoursObj.hours} hours ${totalHoursObj.minutes} minutes`,
        avgHours: `${avgHoursObj.avgHours} hours ${avgHoursObj.avgRemainingMinutes} minutes`,
      });
    };

    fetchWorkHours();
  }, [user, db]);

  return { totalHours, avgHours };
};

export default useWorkHoursSummary;
